/*
 * Back in stock drawer, backed by the Amp Back in Stock JS API (window.BIS).
 * https://help.useamp.com/article/757-javascript-api
 */
class BackInStockDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('.js-back-in-stock-drawer-overlay');
    this.inner = this.querySelector('.back-in-stock-drawer__inner');
    this.closeButtons = this.querySelectorAll('.js-back-in-stock-drawer-close, .js-back-in-stock-drawer-overlay');
    this.form = this.querySelector('#back-in-stock-form');
    this.successEl = this.querySelector('.js-back-in-stock-success');
    this.errorEl = this.querySelector('.js-back-in-stock-error');
    this.descEl = this.querySelector('.js-back-in-stock-desc');
    this.isSubmitting = false;

    this.bindEvents();
  }

  bindEvents() {
    if (this.closeButtons) {
      this.closeButtons.forEach(btn => btn.addEventListener('click', this.close.bind(this)));
    }
    if (this.overlay) {
      this.overlay.addEventListener('click', this.close.bind(this));
    }

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('.js-back-in-stock-trigger');
      if (trigger) {
        e.preventDefault();
        this.resetMessages();

        const fields = {
          '#back-in-stock-variant-id': 'data-variant-id',
          '#back-in-stock-product-id': 'data-product-id',
          '#back-in-stock-item-color': 'data-item-color',
          '#back-in-stock-item-variant': 'data-item-variant',
          '#back-in-stock-item-variant-id': 'data-item-variant-id'
        };
        Object.keys(fields).forEach((selector) => {
          const input = this.querySelector(selector);
          const value = trigger.getAttribute(fields[selector]);
          if (input && value) input.value = value;
        });

        const isFromSizeSelector = trigger.closest('#modal-size-selector');
        if (isFromSizeSelector) {
          this.overlay.classList.add('bg-transparent');
          this.overlay.classList.remove('bg-black/40');
        } else {
          this.overlay.classList.remove('bg-transparent');
          this.overlay.classList.add('bg-black/40');
        }

        this.open();
      }
    });

    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }
  }

  resetMessages() {
    if (this.successEl) this.successEl.classList.add('hidden');
    if (this.errorEl) this.errorEl.classList.add('hidden');
    if (this.form) this.form.classList.remove('hidden');
    if (this.descEl) this.descEl.classList.remove('hidden');
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || this.dataset.errorMessage || 'Something went wrong. Please try again.';
    this.errorEl.classList.remove('hidden');
  }

  showSuccess() {
    if (this.successEl) {
      this.successEl.textContent =
        this.dataset.successMessage || "You're all set! We'll email you as soon as this item is back in stock.";
      this.successEl.classList.remove('hidden');
    }
    if (this.form) this.form.classList.add('hidden');
    if (this.descEl) this.descEl.classList.add('hidden');
  }

  fieldValue(selector) {
    return (this.querySelector(selector)?.value || '').trim();
  }

  setSubmitting(isSubmitting) {
    this.isSubmitting = isSubmitting;
    const submitBtn = this.form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.disabled = isSubmitting;
  }

  // The Amp app embed defines window.BIS asynchronously: submissions wait for it.
  waitForBIS(timeout = 10000) {
    return new Promise((resolve, reject) => {
      const ready = () => window.BIS && typeof window.BIS.create === 'function';
      if (ready()) {
        resolve(window.BIS);
        return;
      }

      const startedAt = Date.now();
      const poll = setInterval(() => {
        if (ready()) {
          clearInterval(poll);
          resolve(window.BIS);
        } else if (Date.now() - startedAt >= timeout) {
          clearInterval(poll);
          reject(new Error('Back in Stock SDK unavailable'));
        }
      }, 100);
    });
  }

  // BIS.create returns its own thenable with a single-callback then(): wrap it, and
  // never leave the form disabled if the callback never fires.
  subscribe(BIS, email, variantId, productId, attributes) {
    return new Promise((resolve, reject) => {
      let request;
      try {
        request = BIS.create(email, variantId, productId, attributes);
      } catch (error) {
        reject(error);
        return;
      }

      if (!request || typeof request.then !== 'function') {
        reject(new Error('Back in Stock returned no response'));
        return;
      }

      const timer = setTimeout(() => reject(new Error('Back in Stock request timed out')), 15000);
      request.then((response) => {
        clearTimeout(timer);
        resolve(response);
      });
    });
  }

  // Consent also enrols the customer, through the same native customer form the
  // newsletter modal posts. Only the redirect is dropped: the drawer stays open.
  subscribeToNewsletter(email) {
    const newsletterForm = this.querySelector('#BackInStockNewsletterForm');
    if (!newsletterForm) return;

    const emailField = newsletterForm.querySelector('input[name="contact[email]"]');
    if (!emailField) return;
    emailField.value = email;

    fetch(newsletterForm.action, {
      method: 'POST',
      body: new FormData(newsletterForm),
      headers: { Accept: 'text/html' }
    })
      .then(() => {
        window.GZAnalyticsQueue = window.GZAnalyticsQueue || [];
        window.GZAnalyticsQueue.push((GZAnalytics) => {
          GZAnalytics.push({ event: 'newsletter_subscription', form_location: 'back_in_stock' });
        });
      })
      .catch((error) => console.error('Newsletter subscription failed:', error));
  }

  // Failures come back as { status: 'Error', errors: { field: ['message'] } }
  formatErrors(response) {
    const errors = response && response.errors;
    if (!errors) return '';
    const errorMessage = Object.keys(errors)
      .map((field) => [].concat(errors[field]).join(', '))
      .join(' ');
      
    if (errorMessage.toLowerCase().includes('already registered')) {
      return this.dataset.alreadyRegisteredMessage || errorMessage;
    }
    
    return errorMessage;
  }

  onSubmit(e) {
    e.preventDefault();
    if (this.isSubmitting) return;

    const emailInput = this.form.querySelector('input[name="email"]');
    const consentInput = this.form.querySelector('input[name="consent"]');
    const email = emailInput ? emailInput.value.trim() : '';
    if (!email) return;

    const variantId = Number(this.fieldValue('#back-in-stock-variant-id'));
    const productId = Number(this.fieldValue('#back-in-stock-product-id'));
    if (!variantId || !productId) {
      this.showError();
      return;
    }

    const wantsNewsletter = !!(consentInput && consentInput.checked);
    this.setSubmitting(true);

    this.waitForBIS()
      .then((BIS) => this.subscribe(BIS, email, variantId, productId, { accepts_marketing: wantsNewsletter }))
      .then((response) => {
        if (!response || response.status !== 'OK') {
          this.showError(this.formatErrors(response));
          return;
        }

        this.showSuccess();
        this.trackNotifyMe(productId, variantId);

        // Best effort: a failed signup must not turn a registered alert into an error.
        if (wantsNewsletter) this.subscribeToNewsletter(email);
      })
      .catch((error) => {
        console.error('Back in stock subscription failed:', error);
        this.showError();
      })
      .finally(() => this.setSubmitting(false));
  }

  trackNotifyMe(productId, variantId) {
    window.GZAnalyticsQueue = window.GZAnalyticsQueue || [];
    window.GZAnalyticsQueue.push((GZAnalytics) => {
      const productEl = document.querySelector('[data-item-id="' + productId + '"]');
      const enrichment = productEl ? GZAnalytics.englishCategories(GZAnalytics.readItemDataset(productEl)) : {};
      GZAnalytics.push({
        event: 'notify_me',
        ecommerce_item_name: enrichment.item_name || null,
        ecommerce_item_id: String(productId),
        ecommerce_item_variant_id: this.fieldValue('#back-in-stock-item-variant-id') || variantId || null,
        ecommerce_item_variant: this.fieldValue('#back-in-stock-item-variant') || enrichment.item_variant || null,
        ecommerce_item_color: this.fieldValue('#back-in-stock-item-color') || enrichment.item_color || null,
        ecommerce_item_category: enrichment.item_category || null,
        ecommerce_item_category2: enrichment.item_category2 || null,
        ecommerce_item_category3: enrichment.item_category3 || null,
        ecommerce_item_category4: enrichment.item_category4 || null,
        ecommerce_item_category5: enrichment.item_category5 || null
      });
    });
  }

  open() {
    this.classList.remove('invisible', 'pointer-events-none');

    // Force reflow
    void this.offsetWidth;

    this.overlay.classList.remove('opacity-0', 'pointer-events-none');
    this.overlay.classList.add('opacity-100', 'pointer-events-auto');

    this.inner.classList.remove('translate-y-full', 'md:translate-x-full');
    this.inner.classList.add('translate-y-0', 'md:translate-x-0');

    document.body.classList.add('overflow-hidden');

    if (!this.focusTrap && window.FocusTrap) {
      this.focusTrap = new FocusTrap(this, {
        onEscape: this.close.bind(this)
      });
    }
    if (this.focusTrap) this.focusTrap.activate();
  }

  close() {
    this.overlay.classList.remove('opacity-100', 'pointer-events-auto');
    this.overlay.classList.add('opacity-0');

    this.inner.classList.remove('translate-y-0', 'md:translate-x-0');
    this.inner.classList.add('translate-y-full', 'md:translate-x-full');

    setTimeout(() => {
      this.classList.add('invisible', 'pointer-events-none');
      document.body.classList.remove('overflow-hidden');
      if (this.focusTrap) this.focusTrap.deactivate();
    }, 300);
  }
}

if (!customElements.get('back-in-stock-drawer')) {
  customElements.define('back-in-stock-drawer', BackInStockDrawer);
}
